<template>
  <svg :id="'pie-chart-'+id" viewBox="0,0,250,250" preserveAspectRatio="xMaxYMax meet">
    <g transform="translate(125, 125)">
      <!-- Draw a gray circle behind the main pie graph, to give the illusion of
      a border. -->
      <circle r="125" fill="#e5e5e5" />

      <!-- Draw an unpopulated tooltip and move it offscreen. -->
      <g class="tooltipg" transform="translate(-500, -500)">
        <circle r="80" fill="white" />
        <circle r="77" class="body" />
        <text fill="white" text-anchor="middle">
          <tspan class="name" x="0" dy="-0.5em"></tspan>
          <tspan class="count" x="0" dy="1.25em"></tspan>
          <tspan class="percent" x="0" dy="1.25em"></tspan>
        </text>
      </g>
    </g>
  </svg>
</template>

<script>
  import * as d3 from 'd3'
  import * as compat from '@/components/mixins/compat'
  import * as geometry from '@/components/mixins/geometry'
  import { default as util } from '@/components/mixins/util'

  export default {
    name: 'PieGraph',
    props: {
      id: {type: String, required: true},
      data: {type: Array, required: true},
      select: {type: Function, default: null},
    },
    data: function() {
      return {
        color: d3.scaleOrdinal(['#98abc5', '#8a89a6', '#7b6888', '#6b486b', '#a05d56', '#d0743c', '#ff8c00']),
        path: d3.arc().outerRadius(122).innerRadius(0),
        pie: d3.pie().sort(null).value(function(d) { return d.count }),
      }
    },
    methods: {
      draw: function() {
        let select = this.select,
          color = this.color,
          path = this.path,
          pie = this.pie

        function key(d) {
          return d.data.name
        }
        // arcTween defines how labels should move during animation. They should
        // smoothly expand/contract to their new size.
        function arcTween(d) {
          let i = d3.interpolate(this._current, d)
          this._current = i(0)
          return function(t) { return path(i(t)) }
        }
        // labelTransformTween defines how slice labels should move during
        // animation. Labels always try to stay in the middle of their slice.
        function labelTransformTween(d) {
          let isWhole = d.data.percent == 1,
            wasWhole = this._current.data && this._current.data.percent == 1

          if (isWhole && wasWhole) {
            this._current = d
            return function(t) { return 'translate(0, 0)' }
          } else if (isWhole) {
            let i = d3.interpolateString('translate('+path.centroid(this._current)+')', 'translate(0, 0)')
            this._current = d
            return i
          } else if (wasWhole) {
            let i = d3.interpolateString('translate(0, 0)', 'translate('+path.centroid(d)+')')
            this._current = d
            return i
          }
          let i = d3.interpolate(this._current, d)
          this._current = i(0)
          return function(t) { return 'translate('+path.centroid(i(t))+')' }
        }

        let svg = d3.select('svg#pie-chart-'+this.id),
          g = svg.select('g'),
          tooltipg = g.select('g.tooltipg'),
          arc = g.selectAll('.arc')

        function arcMouseover(d) {
          if (d.data.percent == 1) {
            return
          }

          let tooltip = tooltipg.select('text')
          tooltip.select('tspan.name').text(d.data.name)
          tooltip.select('tspan.count').text(d.data.count.toLocaleString() + ' certs')
          tooltip.select('tspan.percent').text(util.methods.sprintSmallPercent(d.data.percent))

          tooltipg.select('circle.body').style('fill', color(d.data.name))

          tooltipg.attr('transform', 'translate(0, 0)').raise()
          g.selectAll('text.pie-label').attr('visibility', 'hidden')
        }
        function arcMouseout(d) {
          tooltipg.attr('transform', 'translate(-500, -500)')
          g.selectAll('text.pie-label').attr('visibility', function(d) {
            if (geometry.labelWillFit(path.centroid(d), d)) {
              return 'visible'
            }
            return 'hidden'
          })
        }
        function arcClick(d) {
          if (d.data.percent == 1 || select == null) {
            return
          }
          select(d.data.image)
          arcMouseout(d)
        }

        let data0 = arc.data(), data1 = pie(this.data)

        arc = arc.data(data1, key)

        let arcEnter = arc.enter().append('g').attr('class', 'arc')
        // Create a new slice of pie, whose colors corresponds to its name.
        arcEnter.append('path')
          .each(function(d, i) { this._current = geometry.findArcEntry(i, data0, data1, key) || d })
          .attr('fill', function(d) { return color(d.data.name) })
          .on(compat.mouseover(), arcMouseover).on(compat.mouseout(), arcMouseout).on(compat.click(), arcClick)
        // Create a 3-line label for the slice's name and other info.
        let label = arcEnter.append('text')
          .each(function(d, i) { this._current = geometry.findArcEntry(i, data0, data1, key) || d })
          .attr('class', 'pie-label').attr('fill', 'white').attr('text-anchor', 'middle')
        label.append('tspan').attr('x', 0).attr('dy', '0em')
          .text(function(d) { return d.data.name })
        label.append('tspan').attr('x', 0).attr('dy', '1.25em').attr('class', 'count')
        label.append('tspan').attr('x', 0).attr('dy', '1.25em').attr('class', 'percent')

        let arcUpdate = arcEnter.merge(arc).transition().duration(750)
        arcUpdate.select('path').attrTween('d', arcTween)
          .style('cursor', function(d) { return (d.data.percent == 1 || select == null ? 'default' : 'pointer') })
        label = arcUpdate.select('text')
          .attrTween('transform', labelTransformTween) // Move labels to the middle of their slice.
          .attr('visibility', function(d) { // Hide labels that don't fit w/in their slice.
            if (d.data.percent == 1 || geometry.labelWillFit(path.centroid(d), d)) {
              return 'visible'
            }
            return 'hidden'
          })
        label.select('tspan.count') // If a slice is 100%, write the exact count in a middle line.
          .text(function(d) { return (d.data.percent == 1 ? d.data.count.toLocaleString()+" certs" : "") })
        label.select('tspan.percent') // On the bottom line, write the slice's percentage.
          .text(function(d) { return util.methods.sprintSmallPercent(d.data.percent) })

        let arcExit = arc.exit()
        arcExit.select('text').remove()
        arcExit = arcExit.datum(function(d, i) { return geometry.findArcExit(i, data0, data1, key) || d })
          .transition().duration(750)
        arcExit.select('path').attrTween('d', arcTween)
        arcExit.remove()
      },
    },

    watch: {
      data: function() { this.draw() },
    },
    mounted: function() {
      let path = this.path

      let svg = d3.select('svg#pie-chart-'+this.id)
      svg.select('g.tooltipg')
        .on(compat.mouseover(), function(d) {
          d3.select(this).attr('transform', 'translate(0, 0)')
          svg.selectAll('text.pie-label').attr('visibility', 'hidden')
        })
        .on(compat.mouseout(), function(d) {
          d3.select(this).attr('transform', 'translate(-500, -500)')
          svg.selectAll('text.pie-label').attr('visibility', function(d) {
            if (geometry.labelWillFit(path.centroid(d), d)) {
              return 'visible'
            }
            return 'hidden'
          })
        })

      this.draw()
    },
  }
</script>

<style scoped>
  /* If the window is small enough that each pie gets its own row, put empty
  /* space on either side so they don't look stupid big. */
  @media (max-width: 768px) {
    svg {
      margin-left: 20%;
      margin-right: 20%;

      /* Prevent browser from asking to save/copy image. */
      -webkit-user-select: none;
      -webkit-touch-callout: none;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/widgets/PieGraph.vue